import React, { useEffect, useRef } from'react';
import { gsap } from'../../animations/gsap.config';
import { ScrollTrigger } from'../../animations/gsap.config';
import servicesData from'../../data/services.json';
import { ArrowDown } from'lucide-react';



/* ─────────────────────────────────────────────
 SCROLL INDICATOR
───────────────────────────────────────────── */
const ScrollIndicator = () => {
 const lineRef = useRef(null);

 useEffect(() => {
 gsap.to(lineRef.current, {
 scaleY: 0,
 transformOrigin:'top center',
 duration: 0.8,
 ease:'power2.inOut',
 repeat: -1,
 yoyo: true,
 delay: 0.5,
 });
 }, []);

 return (
 <div className="flex flex-col items-center gap-2">
 <span className="text-[10px] font-medium tracking-[0.3em] uppercase text-slate-500">
 Explore Services
 </span>
 <div
 ref={lineRef}
 className="w-px h-10 bg-gradient-to-b from-primary-400 to-transparent"
 />
 <ArrowDown size={12} className="text-slate-500 animate-bounce" />
 </div>
 );
};

/* ─────────────────────────────────────────────
 MAIN SERVICES HERO
───────────────────────────────────────────── */
const ServicesHero = () => {
 const { hero } = servicesData;

 const sectionRef = useRef(null);
 const imgRef = useRef(null);
 const overlayRef = useRef(null);
 const meshRef = useRef(null);
 const headLine1Ref = useRef(null);
 const headLine2Ref = useRef(null);
 const descRef = useRef(null);
 const statsRef = useRef(null);
 const scrollCueRef = useRef(null);
 const vertLineRef = useRef(null);

 /* ── Entry animation ── */
 useEffect(() => {
 const ctx = gsap.context(() => {
 const tl = gsap.timeline({ delay: 0.3 });

 /* Image reveal */
 tl.fromTo(
 imgRef.current,
 { scale: 1.15, filter:'blur(10px)' },
 { scale: 1.03, filter:'blur(0px)', duration: 2, ease:'power2.out' },
 );

 /* Overlay fade */
 tl.fromTo(
 overlayRef.current,
 { opacity: 0 },
 { opacity: 1, duration: 1.2, ease:'power2.out' },
 0,
 );

 tl.fromTo(
 headLine1Ref.current,
 { y: 80, opacity: 0, skewY: 4 },
 { y: 0, opacity: 1, skewY: 0, duration: 0.9, ease:'power4.out' },
 0.4,
 );

 tl.fromTo(
 headLine2Ref.current,
 { y: 80, opacity: 0, skewY: 4 },
 { y: 0, opacity: 1, skewY: 0, duration: 0.9, ease:'power4.out' },
 0.55,
 );

 tl.fromTo(
 descRef.current,
 { y: 30, opacity: 0 },
 { y: 0, opacity: 1, duration: 0.7, ease:'power3.out' },
 0.8,
 );

 tl.fromTo(
 statsRef.current?.children ?? [],
 { x: -20, opacity: 0 },
 {
 x: 0, opacity: 1,
 stagger: 0.1,
 duration: 0.6,
 ease:'power3.out',
 },
 1.0,
 );

 tl.fromTo(
 scrollCueRef.current,
 { y: 20, opacity: 0 },
 { y: 0, opacity: 1, duration: 0.5, ease:'power3.out' },
 1.3,
 );

 tl.fromTo(
 vertLineRef.current,
 { scaleY: 0 },
 { scaleY: 1, duration: 0.8, ease:'power3.out', transformOrigin:'top' },
 0.7,
 );

 gsap.to(meshRef.current, {
 y: -30,
 x: 20,
 duration: 7,
 ease:'sine.inOut',
 repeat: -1,
 yoyo: true,
 });
 }, sectionRef);

 return () => ctx.revert();
 }, []);

 /* ── Scroll parallax ── */
 useEffect(() => {
 const ctx = gsap.context(() => {
 ScrollTrigger.create({
 trigger: sectionRef.current,
 start:'top top',
 end:'bottom top',
 scrub: true,
 onUpdate: (self) => {
 const p = self.progress;
 gsap.set(imgRef.current, { y: p * 100, scale: 1.03 + p * 0.05 });
 gsap.set(meshRef.current, { y: p * 80 });
 },
 });
 }, sectionRef);

 return () => ctx.revert();
 }, []);

 return (
 <section
 ref={sectionRef}
 id="services-hero"
 className="relative w-full overflow-hidden"
 style={{ height:'85vh', minHeight:'550px', maxHeight:'800px' }}
 >
 {/* ── BACKGROUND ── */}
 <div
 className="absolute inset-0 z-0"
 style={{
 background:'linear-gradient(135deg, #ffffff 0%, #f8fafc 50%, #eff6ff 100%)',
 }}
 />

 <div className="absolute inset-0 z-[1] overflow-hidden">
 <img
 ref={imgRef}
 src={hero.image}
 alt="Our Services"
 className="w-full h-full object-cover will-change-transform"
 style={{ transform:'scale(1.15)' }}
 />
 </div>

 <div
 ref={overlayRef}
 className="absolute inset-0 z-[2]"
 style={{
 background:`
 linear-gradient(to right, rgba(6,13,36,0.96) 0%, rgba(6,13,36,0.7) 45%, rgba(6,13,36,0.3) 100%),
 linear-gradient(to top, rgba(6,13,36,0.96) 0%, transparent 40%),
 linear-gradient(to bottom, rgba(6,13,36,0.5) 0%, transparent 30%)
`,
 opacity: 0,
 }}
 />

 <div
 ref={meshRef}
 className="absolute inset-0 z-[3] pointer-events-none will-change-transform"
 style={{
 background:`
 radial-gradient(ellipse 60% 50% at 70% 60%, rgba(59,130,246,0.15) 0%, transparent 55%),
 radial-gradient(ellipse 40% 40% at 30% 30%, rgba(16,185,129,0.1) 0%, transparent 50%)
`,
 }}
 />

 <div className="absolute inset-0 z-[4] dot-pattern opacity-10 pointer-events-none" />

 {/* ── CONTENT ── */}
 <div className="absolute inset-0 z-20 flex flex-col justify-center px-6 sm:px-12 lg:px-20 xl:px-28">
 <div className="max-w-2xl xl:max-w-3xl space-y-6 pt-24 lg:pt-16">

 <div className="space-y-2 overflow-hidden">
 <div className="overflow-hidden">
 <h1
 ref={headLine1Ref}
 className="font-display font-black leading-none text-white"
 style={{
 fontSize:'clamp(2.5rem, 6vw, 5rem)',
 letterSpacing:'-0.02em',
 opacity: 0,
 }}
 >
 {hero.headline}
 </h1>
 </div>
 <div className="overflow-hidden">
 <h1
 ref={headLine2Ref}
 className="font-display font-black leading-none text-gradient-primary"
 style={{
 fontSize:'clamp(2.5rem, 6vw, 5rem)',
 letterSpacing:'-0.02em',
 opacity: 0,
 }}
 >
 {hero.headlineAccent}
 </h1>
 </div>
 </div>

 <p
 ref={descRef}
 className="text-slate-300 leading-relaxed max-w-lg"
 style={{
 fontSize:'clamp(0.95rem, 1.4vw, 1.1rem)',
 opacity: 0,
 }}
 >
 {hero.description}
 </p>

 <div ref={statsRef} className="flex items-center gap-6 pt-4 flex-wrap">
 {hero.stats.map((stat, i) => (
 <div key={i} className="flex items-center gap-3">
 <div className="text-secondary-400 font-mono font-bold text-2xl">
 {stat.value}
 </div>
 <div className="text-xs text-slate-400 font-medium tracking-wide w-24 leading-tight">
 {stat.label}
 </div>
 {i < hero.stats.length - 1 && (
 <div className="h-8 w-px bg-white/10 hidden sm:block mx-2" />
 )}
 </div>
 ))}
 </div>
 </div>
 </div>

 {/* ── SCROLL CUE ── */}
 <div
 ref={scrollCueRef}
 className="absolute bottom-8 left-1/2 -translate-x-1/2 z-30"
 style={{ opacity: 0 }}
 >
 <ScrollIndicator />
 </div>

 {/* ── DECORATIVE ELEMENTS ── */}
 <div
 ref={vertLineRef}
 className="absolute left-0 top-1/4 bottom-1/4 w-[2px] z-20 hidden lg:block"
 style={{
 background:'linear-gradient(to bottom, transparent, rgba(59,130,246,0.4), transparent)',
 transform:'scaleY(0)',
 transformOrigin:'top',
 }}
 />

 <div className="absolute right-6 top-1/3 hidden xl:flex flex-col items-center gap-2 pointer-events-none z-20">
 <p
 className="font-mono text-[10px] tracking-widest text-slate-400"
 style={{ writingMode:'vertical-lr' }}
 >
 EXPERTISE / OFFERINGS
 </p>
 </div>

 <div
 className="absolute bottom-0 left-0 right-0 h-32 z-10 pointer-events-none"
 style={{
 background:'linear-gradient(to top, #060d24 0%, transparent 100%)',
 }}
 />
 </section>
 );
};

export default ServicesHero;
